import { useCallback, useMemo } from 'react'
import { NavigateFunction, matchPath, useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useProfileRoutesConditions } from '@pages/Profile/ProfileRoutes'
import { useTraderRoomRoutesConditions } from '@pages/Traders-Room/TradersRoom.routes'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useNavigationMode } from '@context/NavigationModeContext'
import { useProductReadContext } from '@context/ProductContext'

import {
  AccountDetailedDto,
  accountHasIntroducingBroker,
  showClosedAccountPage,
  showInactiveAccountPage,
} from '@model/AccountDetailedDto'
import {
  TickmillProductType,
  isTickmillProductTypeCFD,
  isTickmillProductTypeETD,
} from '@model/TickmillProductType'

import { useLandingPage } from '@hooks/useLandingPage'

import { isAccountProofOfIdentificationVerified } from './AccountAccess/accountCompanyStatuses'
import {
  hasConsentedToLegalDocuments,
  isAccountDocumentManagement1MonthExpired,
  isAccountDocumentManagementExpired,
  isAccountPendingDocumentVerificationStatus,
} from './AccountAccess/accountDocumentStatuses'
import {
  getDocumentVerificationPath,
  isDepositBeforeKYC,
  isKYCUpdateRequired,
  isKycVerified,
  isSumsubNeeded,
  requireDepositBeforeKYC,
} from './AccountAccess/accountKYCStatuses'
import {
  hasClearedAppropriatenessTest,
  hasProductActivatedStatus,
  hasProductClosedStatus,
  hasProductDormantStatus,
  hasProductEmailVerifiedStatus,
  hasSubmittedAppropriatenessTest,
  isEconomicProfileTestNeeded,
  showApptestRiskScreen,
} from './AccountAccess/accountProductStatuses'
import { isTickmillPartner } from './companyName.utils'
import { CommonPaths } from './path-common'

export enum BasePaths {
  TakeAppTest = '/dashboard/product-registration',
}

export const getProductRegistrationPath = (productId?: TickmillProductType): string =>
  productId ? `/dashboard/product-registration/${productId}` : BasePaths.TakeAppTest

export const getProductAppropriatenessTestRiskPath = (productId: TickmillProductType): string =>
  `/dashboard/product-registration/${productId}/appropriateness-test/risk`

export const getProductEconomicProfilePath = (productId?: TickmillProductType): string =>
  `${getProductRegistrationPath(productId)}/economic-profile-test`

export const getProductAppTestRiskAcceptancePath = (productId?: TickmillProductType): string =>
  `${getProductRegistrationPath(productId)}/app-test-risk`

export const isFirstDepositPath = (path: string): boolean => path === CommonPaths.FirstTimeDeposit

export const outDatedBrowserRoutes = [
  CommonPaths.TradersRoomWallets,
  '/dashboard/traders-room/trading-accounts',
  '/dashboard/traders-room/transaction-history',
  CommonPaths.TradersRoomBalances,
  '/dashboard/traders-room/transaction-history',
  '/dashboard/subscriptions/market-data',
  '/dashboard/subscriptions/trading-platforms',
  '/dashboard/subscriptions/history',
  '/dashboard/statements/daily',
]

export const isSignUpPath = (path: string): boolean => {
  const signUpPaths = [
    '/sign-up',
    '/logout',
    '/login',
    '/support',
    '/forgot-password',
    '/users/reset-password',
    '/account-password',
  ]
  return signUpPaths.filter((filteredPath) => path.includes(filteredPath)).length > 0
}

export const isSignUpFirstStepsPath = (path: string): boolean => {
  const signUpPaths = ['sign-up']
  return signUpPaths.filter((filteredPath) => path.includes(filteredPath)).length > 0
}

export const isNoTabsPath = (path: string, extraTabs?: string[]): boolean => {
  const noTabsPaths = [
    '/deposit',
    '/withdrawal',
    '/transfer',
    '/conversion',
    '/trading-accounts/live/new',
    '/trading-accounts/demo/new',
    '/change-password',
    '/demo/add-balance',
    '/sign-up/documents-info',
    '/sign-up/verification',
    '/sign-up/first-time-deposit',
    '/sign-up/confirm',
    BasePaths.TakeAppTest,
    CommonPaths.ProductRegistrationLegalDocs,
    '/dashboard/product-registration/kyc-personal-details',
    '/2fa/confirm',
  ]
  const mergedNoTabsPaths = noTabsPaths.concat(extraTabs || [])
  return mergedNoTabsPaths.some((filteredPath) => path.includes(filteredPath))
}

export const useBasePath = (
  isLogoPath = false
): { redirectionLink: string; getBasePath: (account?: AccountDetailedDto) => string } => {
  const { account } = useAccountReadContext()
  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()
  const visible = useProfileRoutesConditions()
  const { navigationMode } = useNavigationMode()

  const { getLandingPage } = useLandingPage()
  const { product, hasInitializedProductState } = useProductReadContext()
  const getBasePath = useCallback(
    (passedAccount?: AccountDetailedDto) => {
      if (!passedAccount) {
        return CommonPaths.Dashboard
      }
      if (isLogoPath && product === TickmillProductType.CFD && navigationMode === 'trader') {
        return CommonPaths.TradersRoomWallets
      }
      if (isLogoPath && product === TickmillProductType.CFD && navigationMode === 'partner') {
        return CommonPaths.IntroducingBrokerDashboard
      }
      if (isLogoPath && product === TickmillProductType.ETD) {
        getLandingPage(product)
      }
      if (!hasConsentedToLegalDocuments(passedAccount, product)) {
        return CommonPaths.ProductRegistrationLegalDocs
      } else if (
        hasProductEmailVerifiedStatus(passedAccount, product) ||
        !hasClearedAppropriatenessTest(passedAccount, product)
      ) {
        if (
          hasSubmittedAppropriatenessTest(passedAccount, product) ||
          hasProductDormantStatus(passedAccount, product) ||
          hasProductClosedStatus(passedAccount, product)
        ) {
          if (showApptestRiskScreen(passedAccount, product)) {
            if (isEconomicProfileTestNeeded(passedAccount, product)) {
              return getProductAppTestRiskAcceptancePath(product)
            }
            return getProductAppropriatenessTestRiskPath(product)
          }
          return BasePaths.TakeAppTest
        } else {
          return BasePaths.TakeAppTest
        }
      } else if (showClosedAccountPage(passedAccount, product)) {
        return CommonPaths.ClosedAccount
      } else if (showInactiveAccountPage(passedAccount, product)) {
        return CommonPaths.InactiveAccount
      } else if (isEconomicProfileTestNeeded(passedAccount, product)) {
        if (showApptestRiskScreen(passedAccount, product)) {
          return getProductAppTestRiskAcceptancePath(product)
        }
        return getProductEconomicProfilePath(product)
      } else if (requireDepositBeforeKYC(passedAccount) && visible.isFirstTimeDepositVisible) {
        return CommonPaths.FirstTimeDeposit
      } else if (isKYCUpdateRequired(passedAccount)) {
        return CommonPaths.KycUpdateDetails
      } else if (
        !hasProductActivatedStatus(passedAccount, product) &&
        !(
          isDepositBeforeKYC(passedAccount) && isAccountProofOfIdentificationVerified(passedAccount)
        )
      ) {
        if (isSumsubNeeded(passedAccount)) {
          if (
            isAccountPendingDocumentVerificationStatus(passedAccount) &&
            isKycVerified(passedAccount, product)
          ) {
            return CommonPaths.AccountPending
          }
          return CommonPaths.Sumsub
        }
        return getDocumentVerificationPath(passedAccount)
      } else if (isAccountDocumentManagement1MonthExpired(passedAccount)) {
        return getDocumentVerificationPath(passedAccount)
      } else if (isAccountDocumentManagementExpired(passedAccount)) {
        return getDocumentVerificationPath(passedAccount)
      } else if (isTradersRoomVisible) {
        return getLandingPage(
          hasInitializedProductState ? product : account?.preferredLandingPage?.tickmillProductId
        )
      } else if (isTickmillPartner(passedAccount)) {
        return CommonPaths.IntroducingBrokerDashboard
      } else if (accountHasIntroducingBroker(passedAccount)) {
        return CommonPaths.IntroducingBrokerWallets
      }
      return CommonPaths.Dashboard
    },
    [
      account?.preferredLandingPage?.tickmillProductId,
      getLandingPage,
      hasInitializedProductState,
      isLogoPath,
      isTradersRoomVisible,
      navigationMode,
      product,
      visible.isFirstTimeDepositVisible,
    ]
  )

  const redirectionLink = useMemo(() => getBasePath(account), [account, getBasePath])

  return {
    redirectionLink,
    getBasePath,
  }
}

export const useProductSwitchPath = (): {
  navigateToNextPath: (product: TickmillProductType) => void
} => {
  const { account } = useAccountReadContext()
  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()

  const navigate = useNavigate()

  const getPath = useCallback(
    (product: TickmillProductType): string => {
      if (!account) {
        return CommonPaths.Dashboard
      } else if (!hasConsentedToLegalDocuments(account, product)) {
        return CommonPaths.ProductRegistrationLegalDocs
      } else if (hasConsentedToLegalDocuments(account, product)) {
        if (isTickmillProductTypeETD(product)) {
          return CommonPaths.TradersRoomBalances
        } else if (isTickmillProductTypeCFD(product)) {
          return CommonPaths.TradersRoomWallets
        }
        return CommonPaths.Dashboard
      } else if (showClosedAccountPage(account, product)) {
        return CommonPaths.ClosedAccount
      } else if (showInactiveAccountPage(account, product)) {
        return CommonPaths.InactiveAccount
      } else if (!hasProductActivatedStatus(account, product)) {
        return getDocumentVerificationPath(account)
      } else if (isAccountDocumentManagement1MonthExpired(account)) {
        return getDocumentVerificationPath(account)
      } else if (isAccountDocumentManagementExpired(account)) {
        return getDocumentVerificationPath(account)
      } else if (isTradersRoomVisible) {
        return CommonPaths.TradersRoomWallets
      } else if (accountHasIntroducingBroker(account)) {
        return CommonPaths.IntroducingBrokerWallets
      } else if (isTickmillPartner(account)) {
        return CommonPaths.IntroducingBrokerWallets
      }

      return CommonPaths.Dashboard
    },
    [account, isTradersRoomVisible]
  )

  return {
    navigateToNextPath: (product) => navigate(getPath(product)),
  }
}

export const useActionPage = (): boolean => {
  const location = useLocation()
  const { isDefaultCFDProductType } = useProductReadContext()
  const paths = useMemo(
    () =>
      isDefaultCFDProductType()
        ? [
            'dashboard/traders-room/deposit/*',
            'dashboard/traders-room/wallets/:id/transfer',
            'dashboard/traders-room/wallets/transfer',
            'dashboard/traders-room/wallets/:id/withdrawal',
            'dashboard/traders-room/wallets/withdrawal',
            'dashboard/traders-room/trading-accounts/:id/demo/add-balance',
            'dashboard/traders-room/trading-accounts/live/new',
            'dashboard/traders-room/trading-accounts/demo/new',
            'profile/bank-accounts/new',
          ]
        : [
            'dashboard/traders-room/deposit/*',
            'dashboard/traders-room/wallets/:id/transfer',
            'dashboard/traders-room/balances/:id/transfer',
            'dashboard/traders-room/wallets/transfer',
            'dashboard/traders-room/wallets/:id/withdrawal',
            'dashboard/traders-room/wallets/withdrawal',
            'dashboard/traders-room/transactions/conversion',
            'profile/bank-accounts/new',
          ],
    [isDefaultCFDProductType]
  )
  return useMemo(
    () => paths.some((path) => matchPath({ path }, location.pathname)),
    [location, paths]
  )
}

export const navigateBasedOnProductType = (
  isProductTypeCFD: boolean,
  navigate: NavigateFunction,
  isPartnerAccount?: boolean
): void => {
  const balancesPath = CommonPaths.TradersRoomBalances

  const selectedPath = isProductTypeCFD
    ? isPartnerAccount
      ? CommonPaths.IntroducingBrokerWallets
      : CommonPaths.TradersRoomWallets
    : balancesPath

  navigate(selectedPath)
}

export { CommonPaths } from './path-common'
